import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * Payroll by API (HHNodeServer `routes/payrollPosting.js`).
 *
 * Managers approve timesheets and leave; payroll then approves each person
 * here, which locks their period and posts it to the connected payroll system
 * (Xero, or MYOB). Xero reports when the pay run is posted; MYOB cannot, so
 * payroll marks those complete by hand. The per-approval state is carried on
 * `TimesheetApprovalRequest.payroll_status`.
 */

export interface PayrollProviderInfo {
  provider: 'xero' | 'myob' | null;
  label?: string;
  can_revert?: boolean;
  /** Xero: completion is detected from posted pay runs. */
  auto_completes?: boolean;
  /** Xero: leave balances can be read back. */
  pulls_balances?: boolean;
  /** False when the connection must be re-made to grant posting access. */
  can_post?: boolean;
  missing_scopes?: string[];
  leave_source?: 'local' | 'xero';
  last_balance_sync_at?: string | null;
}

export interface PayrollApproveResult {
  accepted: number[];
  skipped: { approval_id: number; reason: string }[];
}

// Approving posts inline for up to ~20s before handing the rest to the
// background worker, and each Xero call is rate-limited — well past the 10s default.
const SLOW = { timeout: 60_000 };

class PayrollPostingService {
  async getProvider(): Promise<ApiResponse<PayrollProviderInfo>> {
    return apiService.get('/payroll-posting/provider');
  }

  /** Problems that would stop each approval posting (unlinked, unmapped…). */
  async readiness(approvalIds: number[]): Promise<ApiResponse<Record<number, string[]>>> {
    return apiService.post('/payroll-posting/readiness', { approval_ids: approvalIds });
  }

  async approve(approvalIds: number[]): Promise<ApiResponse<PayrollApproveResult>> {
    return apiService.post('/payroll-posting/approve', { approval_ids: approvalIds }, undefined, SLOW);
  }

  async retry(approvalIds: number[]): Promise<ApiResponse<{ retried: number[] }>> {
    return apiService.post('/payroll-posting/retry', { approval_ids: approvalIds }, undefined, SLOW);
  }

  async unapprove(approvalId: number): Promise<ApiResponse<void>> {
    return apiService.post('/payroll-posting/unapprove', { approval_id: approvalId }, undefined, SLOW);
  }

  async markCompleted(approvalIds: number[]): Promise<ApiResponse<{ completed: number[] }>> {
    return apiService.post('/payroll-posting/mark-completed', { approval_ids: approvalIds });
  }

  async checkCompletion(): Promise<ApiResponse<{ completed: number[] }>> {
    return apiService.post('/payroll-posting/check-completion', {}, undefined, SLOW);
  }
}

export const payrollPostingService = new PayrollPostingService();
export default payrollPostingService;
